import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import PayPalButtonDisplay, { UpdateShouldReauthorizePayPal } from "../PayPal";
import { useEventListener, Icon } from "@qgiv/core-react";

import "./PaymentPaypal.scss";
import { getModifiedPayPalButtonDisplayProps } from "../PayPal/helpers";

let paypalPopupWindow = null;

const PaymentPaypal = ({
    authenticatedMessage = "You're all set! If your total changes, we'll ask you to authenticate with PayPal again.",
    authenticationAmount = 0,
    defaultMessage = "You'll be redirected to your PayPal account, then returned here to complete your gift.",
    isRecurring = false,
    handleCompletePayPalAuthorizationFlow = () => {},
    handleInitiatePayPalAuthorizationFlow = () => {},
    handleLegacyPayPalReset = () => {},
    handleResetPayPalAuthorizationFlow = () => {},
    hasCompletedPayPalAuthorizationFlow = false,
    hasInitiatedPayPalAuthorizationFlow = false,
    path = "",
    payPalButtonDisplayProps = {},
    payPalPaymentSource = "paypal",
}) => {
    const [amount, setAmount] = useState(authenticationAmount);
    const [recurring, setRecurring] = useState(isRecurring);
    const [defaultMessageText, setDefaultMessageText] = useState(
        "You'll be redirected to your PayPal account, then returned here to complete your gift.",
    );
    const [authenticatedMessageText, setAuthenticatedMessageText] = useState(
        "You're all set! If your total changes, we'll ask you to authenticate with PayPal again.",
    );
    const paypalPopupPath = `/common/paypal/express.php?key=${path}&amount=${amount}${
        recurring ? "&recurring=true" : ""
    }`;
    const paypalPopupOptions = `width=600,height=600,resizable=yes,menubar=no,location=no`;
    const { payPalSDKScriptProviderProps = {} } = payPalButtonDisplayProps;

    const handleButtonClick = (e) => {
        e.preventDefault();

        paypalPopupWindow = window.open(
            paypalPopupPath,
            "paypalWindow",
            paypalPopupOptions,
        );
        paypalPopupWindow.focus();
    };

    // -------------------------------------------------------------------------
    // NOTE:
    // In addition to handling the postMessage that is sent from PayPal the try
    // catch has been added to handle any non-JSON messages that are sent to the
    // window. This use case was highlighted by a message that was being sent by
    // React DevTools to the window that was not being sent via JSON and was
    // constantly triggering the logging of a very distracting TypeError.
    // -------------------------------------------------------------------------
    // bind postMessage event listener for PayPal authentication window
    useEventListener("message", (e) => {
        if (e.data.action) {
            return;
        }
        const parseResponse = (e) => {
            try {
                const result = e.data ? JSON.parse(e.data) : null;
                if (result && result["event"]) {
                    return result;
                } else {
                    return null;
                }
            } catch (e) {
                return null;
            }
        };

        // Extract the JSON data from the message
        const response = parseResponse(e);

        // Update form values, display the different payment method button
        // display the authorized message and close the PayPal window.
        // setFieldValue is used here to ensure that we are not overwriting the
        // Formik data with the values object that existed when the component
        // first rendered.
        if (response && response.event === "paypal") {
            const payPalValues = {
                PayPal_Token: response.token,
                PayPal_Payer_ID: response.payerID,
            };
            const paymentSource = "paypal";

            // Handle all of the platform specific UI updates that affect
            // application state after initiating the PayPal SDK request. This
            // includes setting the appropriate form values, displaying the
            // authorized message with the appropriate payment source and
            // closing the pop up window
            handleInitiatePayPalAuthorizationFlow(true);
            handleCompletePayPalAuthorizationFlow({
                paymentSource,
                payPalValues,
            });
            paypalPopupWindow.close();
        }
    });

    useEffect(() => {
        const amountChanged = authenticationAmount !== amount;
        const frequencyChanged = isRecurring !== recurring;

        // if amount or frequency is changed, reset authorization by triggering
        // the payment method reset count which runs
        // handleResetPayPalAuthorizationFlow() in
        // UpdateShouldReauthorizePayPal
        if (
            hasCompletedPayPalAuthorizationFlow &&
            (amountChanged || frequencyChanged)
        ) {
            handleLegacyPayPalReset();
        }

        // update local state to new values
        setAmount(authenticationAmount);
        setRecurring(isRecurring);
    }, [authenticationAmount, isRecurring]);

    // Trade out default and authenticated messages if they are not empty strings
    useEffect(() => {
        if (defaultMessage) {
            setDefaultMessageText(defaultMessage);
        }
        if (authenticatedMessage) {
            setAuthenticatedMessageText(authenticatedMessage);
        }
    }, [defaultMessage, authenticatedMessage]);

    // Arrange all of the data that we need to pass to the
    // PayPalButtonDisplay component
    const modifiedPayPalButtonDisplayOptions = {
        handleCompletePayPalAuthorizationFlow,
        handleInitiatePayPalAuthorizationFlow,
        defaultMessageText,
        handleButtonClick,
        payPalButtonDisplayProps,
        payPalSDKScriptProviderProps,
    };
    // Returns a new object that includes event handlers and data from local
    // component state along the data that was extracted from application state
    const modifiedPayPalButtonDisplayProps =
        getModifiedPayPalButtonDisplayProps(modifiedPayPalButtonDisplayOptions);

    // We should not display the PayPal specific
    // hasCompletedPayPalAuthorizationFlow message when using the new PayPal
    // button as the new PayPal button supports both PayPal and Venmo
    const { displayPayPalSDKScriptProvider } = modifiedPayPalButtonDisplayProps;

    // Consolidate all of the props that need to be passed to the update
    // component into a single object
    const updateShouldReauthorizePayPalProps = {
        hasCompletedPayPalAuthorizationFlow,
        hasInitiatedPayPalAuthorizationFlow,
        handleResetPayPalAuthorizationFlow,
        ...modifiedPayPalButtonDisplayProps,
    };

    // Ensure that the amount displayed to the user never includes more than
    // two decimal places
    const formattedAuthenticatedAmount =
        parseFloat(authenticationAmount).toFixed(2);

    // Display the appropriate logo based on whether the user paid using PayPal
    // or Venmo
    const paymentSourceLogo =
        payPalPaymentSource === "venmo"
            ? "/resources/core/images/logo-venmo-horizontal.png"
            : "/resources/core/images/logo-paypal-horizontal.png";

    // -------------------------------------------------------------------------
    // MARK: Main View Component
    // -------------------------------------------------------------------------
    return (
        <div className="-text--center">
            <UpdateShouldReauthorizePayPal
                {...updateShouldReauthorizePayPalProps}
            />
            {hasCompletedPayPalAuthorizationFlow ? (
                <>
                    <div className="paypal__authenticated">
                        <Icon
                            className="-margin-right--10"
                            glyph="check-solid"
                            type="FontAwesome"
                        />
                        <span className="paypal__authenticated__text">
                            Authenticated for ${formattedAuthenticatedAmount}
                        </span>
                        <img
                            src={paymentSourceLogo}
                            alt="Payment Source"
                            title="Payment Source"
                            className="paypal__payment-source-logo"
                        />
                    </div>
                    {/* Do not display this message if we are using the SDK */}
                    {/* This is because the SDK supports both PayPal & Venmo  */}
                    {!displayPayPalSDKScriptProvider && (
                        <p className="-margin-top--10">
                            {authenticatedMessageText}
                        </p>
                    )}
                </>
            ) : (
                <PayPalButtonDisplay {...modifiedPayPalButtonDisplayProps} />
            )}
        </div>
    );
};

PaymentPaypal.propTypes = {
    authenticatedMessage: PropTypes.string,
    authenticationAmount: PropTypes.number.isRequired,
    defaultMessage: PropTypes.string,
    handleCompletePayPalAuthorizationFlow: PropTypes.func.isRequired,
    handleInitiatePayPalAuthorizationFlow: PropTypes.func.isRequired,
    handleLegacyPayPalReset: PropTypes.func.isRequired,
    handleResetPayPalAuthorizationFlow: PropTypes.func.isRequired,
    hasCompletedPayPalAuthorizationFlow: PropTypes.bool.isRequired,
    hasInitiatedPayPalAuthorizationFlow: PropTypes.bool.isRequired,
    isRecurring: PropTypes.bool.isRequired,
    path: PropTypes.string.isRequired,
    payPalButtonDisplayProps: PropTypes.shape({
        displayPayPalSDKScriptProvider: PropTypes.bool.isRequired,
        payPalSDKScriptProviderProps: PropTypes.shape({
            forceReRender: PropTypes.arrayOf(
                PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.number,
                    PropTypes.bool,
                ]).isRequired,
            ).isRequired,
            getDataForCreateOrder: PropTypes.func.isRequired,
            getDataForCreateBillingAgreement: PropTypes.func.isRequired,
            isCms: PropTypes.bool.isRequired,
            isRecurringDonation: PropTypes.bool.isRequired,
            payPalScriptProviderOptions: PropTypes.shape({
                "client-id": PropTypes.string.isRequired,
                "merchant-id": PropTypes.string.isRequired,
                "enable-funding": PropTypes.string.isRequired,
                "disable-funding": PropTypes.string.isRequired,
                "data-partner-attribution-id": PropTypes.string.isRequired,
                intent: PropTypes.string.isRequired,
            }).isRequired,
            shouldLoadPayPalSDKScriptProvider: PropTypes.bool.isRequired,
        }).isRequired,
    }).isRequired,
    payPalPaymentSource: PropTypes.string.isRequired,
};

export default PaymentPaypal;
